import { ArrowUpRight, ArrowDownLeft, Calendar } from "lucide-react";
import { memo, useMemo } from "react";
import { formatCurrency, formatDate } from "@/config/locale";
import { t } from "./wallet-i18n";

export interface Transaction {
  id: string;
  type: "receivable" | "payable" | "received" | "paid";
  title: string;
  description: string;
  amount: number;
  date: Date;
  status: "pending" | "completed" | "cancelled";
  /** Unix timestamp when funds become available (for pending); show "Disponível em DD/MM". */
  availableOn?: number;
}

interface TransactionItemProps {
  transaction: Transaction;
  showBalance: boolean;
}

function getStatusConfig() {
  return {
    completed: { color: "text-green-600", text: t("statusCompleted") },
    cancelled: { color: "text-red-600", text: t("statusCancelled") },
    pending: { color: "text-yellow-600", text: t("statusPending") },
  } as const;
}

export const TransactionItem = memo(function TransactionItem({ 
  transaction, 
  showBalance 
}: TransactionItemProps) {
  const isPositive = transaction.type === "receivable" || transaction.type === "received";
  const isPending = transaction.status === "pending";
  const statusInfo = getStatusConfig()[transaction.status];

  const formattedAmount = useMemo(() => 
    formatCurrency(transaction.amount), 
    [transaction.amount]
  );

  const formattedDate = useMemo(() => 
    formatDate(transaction.date), 
    [transaction.date]
  );

  return (
    <div className="flex items-start gap-4 p-4 border-b border-border last:border-b-0">
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          isPositive ? "bg-green-100" : "bg-red-100"
        }`}
      >
        {isPositive ? (
          <ArrowDownLeft className="w-5 h-5 text-green-600" />
        ) : (
          <ArrowUpRight className="w-5 h-5 text-red-600" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium truncate">{transaction.title}</h3>
            <p className="text-sm text-muted-foreground truncate">
              {transaction.description}
            </p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Calendar className="w-3 h-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                {formattedDate}
              </span>
              {isPending && (
                <>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className={`text-xs ${statusInfo.color}`}>
                    {statusInfo.text}
                  </span>
                  {transaction.availableOn != null && transaction.availableOn > 0 && (
                    <>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">
                        {t("availableOn")} {formatDate(new Date(transaction.availableOn * 1000))}
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          <div
            className={`text-right font-medium ${
              isPositive ? "text-green-600" : "text-red-600"
            }`}
          >
            {showBalance 
              ? `${isPositive ? "+" : "-"}${formattedAmount}`
              : `${isPositive ? "+" : "-"}R$ •••`
            }
          </div>
        </div>
      </div>
    </div>
  );
});